import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  CheckCircle,
  Circle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Home,
  X,
  BarChart2,
  Clock,
  Navigation,
  Printer,
  Calendar,
  CornerDownRight,
} from "react-feather";
import dayjs from "dayjs";
import Select from "react-select";
import { useGetCompanyInfoQuery } from "../../settings/settingsApi";
import AttendanceDayPrintModal from "../components/attendance/AttendanceDayPrintModal";
import { MAROON } from "../../../components/common/brandColors";
import { useEmployeePhotoCache } from "../../masters/employees/useEmployeePhotoCache";
import { paths } from "../../../routes/paths";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import ShiftChips from "../components/attendance/ShiftChips";
import AttendanceDonut, {
  AttendanceStatRow,
  ScopeChip,
  MARKED_GREEN,
  NOT_MARKED_RED,
} from "../components/attendance/AttendanceDonut";
import {
  useGetAttendanceSummaryQuery,
  useListAttendanceAssignmentsQuery,
  useListAttendanceMismatchesQuery,
  useListAttendancePerformanceQuery,
  useListAttendanceSupervisorAreasQuery,
} from "../guardingApi";

/**
 * Attendance Dashboard - React port of the Flutter screen of the same name
 * (2026-09-09), placed under Guarding beside Guard Lookup: it answers "are my
 * guards on post right now", which is guarding supervision rather than HR
 * attendance.
 *
 * Same shape as the mobile screen, and deliberately so - the two are read by the
 * same people about the same shift, and a figure that differs between them would
 * be worse than useless. One donut (Marked vs Total), expanding into Marked / Not
 * Marked grouped Customer -> duty slot, plus a separate Mismatches section
 * grouped Employee -> assigned vs actually-marked customer.
 *
 * Scope is the server's, not the header's: none of these endpoints take a
 * branchId. A plain supervisor (Attendance.Mark) sees only their assigned areas;
 * anyone holding Attendance.View sees their whole branch scope. The chip on the
 * summary card says which, from `isScopedToMyAreas`.
 *
 * Refreshed every 90 seconds (RTK Query pollingInterval), the same live-screen
 * cadence the Flutter app uses - a supervisor marking on a phone must not leave a
 * manager watching a frozen figure. Polling keeps the previous data on screen
 * while it refetches, so the numbers update in place and nothing a user has
 * expanded is disturbed.
 */
const REFRESH_MS = 90000;

const GROUPINGS = [
  { value: "AREA", label: "By Area" },
  { value: "SUPERVISOR", label: "By Supervisor" },
];

const initialsOf = (first, last) => {
  const a = (first || "").trim().charAt(0);
  const b = (last || "").trim().charAt(0);
  return (a + b).toUpperCase() || "?";
};

const fullName = (first, last) => [first, last].filter(Boolean).join(" ").trim();

// Time only when the mark was made on the shift's own date; a night-shift mark
// made after midnight also names the day, or "02:10" would read as the wrong night.
const formatMarkedAt = (capturedDate, attendanceDate) => {
  if (!capturedDate) return null;
  const at = dayjs(capturedDate);
  return attendanceDate && at.format("YYYY-MM-DD") !== attendanceDate ? at.format("D MMM, h:mm A") : at.format("h:mm A");
};

// Opens turn-by-turn directions from wherever the viewer is to the spot the
// supervisor scanned the customer's QR from before marking.
const directionsUrl = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

// The earliest and latest marking time inside one customer panel, so the
// heading says when that site was worked without having to open it.
// Only a MARKED slot has a capturedDate, so a panel with nothing marked has no
// window at all (null) rather than a window of zero - the two mean different
// things and the heading renders them differently.
const markWindow = (rows) => {
  const times = rows
    .filter((r) => r.isMarked && r.capturedDate)
    .map((r) => dayjs(r.capturedDate).valueOf())
    .filter((n) => Number.isFinite(n));
  if (times.length === 0) return null;
  return { min: Math.min(...times), max: Math.max(...times) };
};

// A travel time, as a supervisor would say it: "15m", "1h 05m". Under a minute
// is "<1m" rather than "0m" - two marks seconds apart is a real answer, and
// rounding it to zero would read as "no gap recorded".
const formatGap = (ms) => {
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return "<1m";
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  return `${hours}h ${String(totalMinutes % 60).padStart(2, "0")}m`;
};

// Colour the travel time by how long it took, so a slow hop is visible while
// scrolling rather than something you have to read every badge to find. Bands
// match how this business talks about it: a few minutes is the next gate down
// the road, up to ~45 minutes is a real drive across town, beyond that is worth
// asking about.
const gapTone = (ms) => {
  const minutes = ms / 60000;
  if (minutes <= 15) return { fg: "#0B7A4B", bg: "#E6F5EE" };
  if (minutes <= 45) return { fg: "#B26A00", bg: "#FDF1DF" };
  return { fg: NOT_MARKED_RED, bg: "#FCE9E9" };
};

// How long it took to reach this customer from the one before - after the
// customer's own name, deliberately the loudest thing on the heading.
const GapBadge = ({ ms }) => {
  const tone = gapTone(ms);
  return (
    <span
      className="d-inline-flex align-items-center flex-shrink-0"
      style={{
        fontSize: 11.5,
        fontWeight: 800,
        color: tone.fg,
        background: tone.bg,
        border: `1px solid ${tone.fg}40`,
        borderRadius: 999,
        padding: "2px 8px",
        lineHeight: 1.35,
        gap: 3,
      }}
      title="Time taken to reach this customer from the previous one (this customer's first mark minus the previous customer's last mark)"
    >
      <CornerDownRight size={12} strokeWidth={2.75} />
      {formatGap(ms)}
    </span>
  );
};

// Latest mark first. An unmarked slot has no time to place on the timeline, so
// it sinks below every marked one instead of sorting as if it were the oldest.
const byMarkedDesc = (a, b) => {
  const at = a.isMarked && a.capturedDate ? dayjs(a.capturedDate).valueOf() : null;
  const bt = b.isMarked && b.capturedDate ? dayjs(b.capturedDate).valueOf() : null;
  if (at != null && bt != null) return bt - at;
  if (at != null) return -1;
  if (bt != null) return 1;
  return (a.assignmentName || "").localeCompare(b.assignmentName || "");
};

// Green only at 100%. Everything short of that is amber or red by how far short -
// a 90%-marked area still has guards nobody has accounted for. Same bands as the
// Flutter screen, so the two colour the same area the same way.
const percentColor = (percent) => (percent >= 100 ? MARKED_GREEN : percent >= 50 ? "#B26A00" : NOT_MARKED_RED);

const Avatar = ({ photoUrl, first, last, size = 30 }) =>
  photoUrl ? (
    <img
      src={photoUrl}
      alt=""
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <div
      className="d-flex align-items-center justify-content-center flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#EEF1F4",
        color: "#5A6472",
        fontSize: size * 0.38,
        fontWeight: 700,
      }}
    >
      {initialsOf(first, last)}
    </div>
  );

// Temp (amber, has a real end date) vs Permanent (green, open-ended). Ported from
// the Flutter TempPermanentBadge so an assignment reads the same on both clients.
const TempBadge = ({ isTemp }) => (
  <span
    className="badge flex-shrink-0"
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: isTemp ? "#B26A00" : MARKED_GREEN,
      background: isTemp ? "#FDF3E3" : "#E8F5EE",
      border: `1px solid ${isTemp ? "#B26A0033" : `${MARKED_GREEN}33`}`,
    }}
  >
    {isTemp ? "Temp" : "Permanent"}
  </span>
);

// One of the three collapsible sections. At most one is open at a time - an
// accordion, as on mobile, so the page stays readable rather than showing three
// long lists at once.
const Section = ({ title, subtitle, icon: Icon, color, count, isOpen, onToggle, children }) => (
  <div className="card mb-3" style={{ borderColor: `${color}33` }}>
    <button
      type="button"
      className="btn text-start w-100 d-flex align-items-center gap-2 px-3 py-3"
      onClick={onToggle}
    >
      <Icon size={18} color={color} />
      <span className="fw-bold" style={{ fontSize: 15 }}>{title}</span>
      {count != null && (
        <span
          className="badge rounded-pill"
          style={{ background: `${color}1F`, color, fontWeight: 700, fontSize: 12 }}
        >
          {count}
        </span>
      )}
      <span className="ms-auto">{isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
    </button>
    {subtitle && <div className="px-3 pb-2 text-muted" style={{ fontSize: 11.5, marginTop: -8 }}>{subtitle}</div>}
    {isOpen && <div className="card-body pt-0">{children}</div>}
  </div>
);

const todayValue = () => dayjs().format("YYYY-MM-DD");

// Area names are matched the way the dashboard procs match them: per branch, by
// name. SQL's = ignores case and trailing spaces here, so the client does too.
const areaKey = (branchId, area) => `${branchId}|${(area || "").trim().toUpperCase()}`;
const customerKey = (row) => `${row.branchId}-${row.custId}`;
const NO_SUPERVISOR = "__none__";

const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const AttendanceDashboardPage = () => {
  const { selectedBranchId } = useBranchFilter();
  // null = "Now": no shift parameter, so the server resolves the running shift.
  const [shift, setShift] = useState(null);
  const [date, setDate] = useState(todayValue);
  const [grouping, setGrouping] = useState("AREA");
  // Filters (2026-09-17). Area and supervisor are also set by tapping a row in
  // the Performance breakdown, so the two ways of narrowing are one state.
  const [area, setArea] = useState(null);
  const [supervisor, setSupervisor] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  // Which Performance row has its detail open underneath it (2026-09-18).
  // Before this, tapping an area only filtered the Marked/Not Marked sections
  // further down the page, so reading one area meant scrolling past everything
  // to the bottom and back. One at a time, like the sections themselves.
  const [openPerfRow, setOpenPerfRow] = useState(null);

  const isToday = date === todayValue();
  // A past day has no "running shift", so Now is not offered for it.
  const changeDate = (value) => {
    if (!value) return;
    setDate(value);
    if (value !== todayValue() && shift == null) setShift("Day");
  };

  // Header branch (2026-10-02): every read follows it; null = all the caller's branches.
  const branchParam = selectedBranchId != null ? { branchId: selectedBranchId } : {};
  const params = { ...(isToday ? {} : { attendanceDate: date }), ...(shift ? { shift } : {}), ...branchParam };
  // Live refresh only makes sense for today; a closed day does not change.
  const poll = { pollingInterval: isToday ? REFRESH_MS : 0 };

  const { data: summary, isLoading: loadingSummary, error: summaryError } = useGetAttendanceSummaryQuery(params, poll);
  const { data: assignments = [], isFetching: loadingAssignments } = useListAttendanceAssignmentsQuery(params, poll);
  const { data: mismatches = [], isFetching: loadingMismatches } = useListAttendanceMismatchesQuery(params, poll);
  const { data: performance = [], isFetching: loadingPerformance } = useListAttendancePerformanceQuery(
    { ...params, groupBy: grouping },
    poll
  );
  const isManager = summary && !summary.isScopedToMyAreas;
  // Older API builds do not have this read; the supervisor filter simply stays hidden.
  const { data: supervisorAreas = [] } = useListAttendanceSupervisorAreasQuery(branchParam, { skip: !isManager });

  // Photos for everyone on screen, fetched once per employee however many rows
  // they appear on.
  const photoRows = useMemo(
    () => [...assignments.filter((a) => a.employeeRecordGuid), ...mismatches],
    [assignments, mismatches]
  );
  const photoUrls = useEmployeePhotoCache(photoRows);

  // Supervisor -> the branch|area keys they cover, and every covered key (for
  // the "(No supervisor assigned)" bucket).
  const { areasBySupervisor, coveredKeys, supervisorOptions } = useMemo(() => {
    const bySup = new Map();
    const covered = new Set();
    const names = new Map();
    supervisorAreas.forEach((p) => {
      const guid = String(p.employeeRecordGuid).toLowerCase();
      const key = areaKey(p.branchId, p.areaName);
      if (!bySup.has(guid)) bySup.set(guid, new Set());
      bySup.get(guid).add(key);
      covered.add(key);
      names.set(guid, `${p.supervisorName || "Unnamed"} (${p.empCode})`);
    });
    const options = [...names.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (options.length) options.push({ value: NO_SUPERVISOR, label: "(No supervisor assigned)" });
    return { areasBySupervisor: bySup, coveredKeys: covered, supervisorOptions: options };
  }, [supervisorAreas]);

  const inSupervisor = (branchId, areaName) => {
    if (!supervisor) return true;
    const key = areaKey(branchId, areaName);
    if (supervisor === NO_SUPERVISOR) return !coveredKeys.has(key);
    return Boolean(areasBySupervisor.get(supervisor)?.has(key));
  };
  const inArea = (areaName) => !area || (areaName || "").trim().toUpperCase() === area.trim().toUpperCase();

  // Each filter narrows the options of the ones after it: supervisor -> area -> customer.
  const supervisorRows = assignments.filter((r) => inSupervisor(r.branchId, r.area));
  const areaOptions = [...new Set(supervisorRows.map((r) => (r.area || "").trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map((a) => ({ value: a, label: a }));
  const areaRows = supervisorRows.filter((r) => inArea(r.area));
  const customerOptions = [...new Map(areaRows.map((r) => [customerKey(r), r.custName || "Unknown Customer"])).entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const filteredRows = areaRows.filter((r) => !customer || customerKey(r) === customer);

  const filteredMismatches = mismatches.filter(
    (m) =>
      inSupervisor(m.assignedBranchId, m.assignedArea) &&
      inArea(m.assignedArea) &&
      (!customer || `${m.assignedBranchId}-${m.assignedCustId}` === customer)
  );

  const hasFilter = Boolean(area || supervisor || customer);
  const clearFilters = () => {
    setArea(null);
    setSupervisor(null);
    setCustomer(null);
  };

  const markedRows = filteredRows.filter((a) => a.isMarked);
  const notMarkedRows = filteredRows.filter((a) => !a.isMarked);
  const mismatchEmployeeCount = new Set(filteredMismatches.map((m) => m.employeeRecordGuid)).size;

  // The server's figures when nothing is filtered; otherwise the same counts
  // taken from the filtered slots (the two lists are the same slot set).
  const figures = hasFilter
    ? {
        total: filteredRows.length,
        marked: markedRows.length,
        notMarked: notMarkedRows.length,
        percent: filteredRows.length ? (markedRows.length / filteredRows.length) * 100 : 0,
      }
    : {
        total: summary?.totalAssignments || 0,
        marked: summary?.markedCount || 0,
        notMarked: summary?.notMarkedCount || 0,
        percent: summary?.percentComplete || 0,
      };

  const filterText = [
    supervisor && `Supervisor: ${supervisorOptions.find((o) => o.value === supervisor)?.label || ""}`,
    area && `Area: ${area}`,
    customer && `Customer: ${customerOptions.find((o) => o.value === customer)?.label || ""}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const printBranchIds = [...new Set(filteredRows.map((r) => r.branchId))];
  const printBranchId = printBranchIds.length === 1 ? printBranchIds[0] : null;
  const { data: companyInfo } = useGetCompanyInfoQuery(printBranchId, { skip: !showPrint || printBranchId == null });

  const toggleSection = (key) => setOpenSection((prev) => (prev === key ? null : key));
  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Grouping by customer for the two assignment lists. Keyed by branch + custId
  // so two customers sharing a name stay apart.
  //
  // Ordered as a timeline, newest first (2026-09-18): the panels sort by their
  // LATEST mark descending, the slots inside each panel do the same, and each
  // panel carries the gap to the panel above it. A panel with nothing marked has
  // no place on a timeline, so it goes last, alphabetically among its peers.
  const groupByCustomer = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
      const key = customerKey(row);
      if (!groups.has(key)) groups.set(key, { key, custName: row.custName || "Unknown Customer", rows: [] });
      groups.get(key).rows.push(row);
    });

    const list = [...groups.values()].map((group) => ({
      ...group,
      rows: [...group.rows].sort(byMarkedDesc),
      window: markWindow(group.rows),
    }));

    list.sort((a, b) => {
      if (a.window && b.window) return b.window.max - a.window.max;
      if (a.window) return -1;
      if (b.window) return 1;
      return a.custName.localeCompare(b.custName);
    });

    /*
      How long the visitor took to GET here from the site before it - so the gap
      belongs to the customer they arrived at, not the one they left:

          this panel's FIRST mark  -  the previous site's LAST mark

      The list is newest-first, so the chronologically previous site is the panel
      BELOW this one, not above it. Worked example from the live screen: Nyali
      Bazaar's last punch 7:30, Lucky Steel's first punch 7:40 -> Lucky Steel
      shows +10m; Seweco's first punch 7:42 -> Seweco shows +2m. The oldest panel
      has nothing before it, so it carries no gap.

      `.find` rather than `list[index + 1]`: a panel with nothing marked has no
      window and would otherwise break the chain for everything below it.
    */
    return list.map((group, index) => {
      const previous = list.slice(index + 1).find((g) => g.window);
      const gapMs = previous && group.window ? group.window.min - previous.window.max : null;
      return { ...group, gapMs: gapMs != null && gapMs > 0 ? gapMs : null };
    });
  };

  // A Performance row sets the same filter the dropdowns do.
  const isFocused = (row) =>
    grouping === "AREA"
      ? row.groupKey != null && area != null && inArea(row.groupKey)
      : supervisor === (row.groupKey == null ? NO_SUPERVISOR : String(row.groupKey).toLowerCase());
  const focusRow = (row) => {
    setCustomer(null);
    if (grouping === "AREA") {
      if (row.groupKey == null) return;
      setArea(isFocused(row) ? null : row.groupKey.trim());
    } else {
      const value = row.groupKey == null ? NO_SUPERVISOR : String(row.groupKey).toLowerCase();
      setArea(null);
      setSupervisor(supervisor === value ? null : value);
    }
  };

  /*
    The slots behind ONE Performance row, resolved from that row alone rather
    than from the page filters. The inline panel answers "what is in this row",
    so it must not depend on whether focusRow could set a filter for it - the
    "(No supervisor assigned)" bucket and unnamed-area rows have no filter to
    set, and those are exactly the rows most worth opening.
  */
  const rowsForPerfRow = (perfRow) => {
    if (grouping === "AREA") {
      if (perfRow.groupKey == null) return assignments.filter((r) => !(r.area || "").trim());
      const wanted = String(perfRow.groupKey).trim().toUpperCase();
      return assignments.filter((r) => (r.area || "").trim().toUpperCase() === wanted);
    }
    const value = perfRow.groupKey == null ? NO_SUPERVISOR : String(perfRow.groupKey).toLowerCase();
    return assignments.filter((r) => {
      const key = areaKey(r.branchId, r.area);
      if (value === NO_SUPERVISOR) return !coveredKeys.has(key);
      return Boolean(areasBySupervisor.get(value)?.has(key));
    });
  };

  // Tapping a Performance row does both jobs it should: opens its own detail
  // right there, and narrows the rest of the page as it always has.
  const togglePerfRow = (row) => {
    setOpenPerfRow((prev) => (prev === row.groupName ? null : row.groupName));
    focusRow(row);
  };

  if (summaryError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">
            Could not load the attendance dashboard. {summaryError?.message || ""}
          </div>
        </div>
      </div>
    );
  }

  const dayLabel = isToday ? "Today's Attendance" : `Attendance · ${dayjs(date).format("ddd, D MMM YYYY")}`;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Attendance Dashboard</h4>
              <h6>Who is marked on post, and who is not - for any day</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-1"
              disabled={!summary || loadingAssignments}
              onClick={() => setShowPrint(true)}
            >
              <Printer size={15} /> Print
            </button>
            <Link
              to={paths.guardingGuardLookup}
              className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
            >
              <Search size={15} /> Guard Lookup
            </Link>
          </div>
        </div>

        {/* Page-level controls: every figure and list below follows them. */}
        <div className="card mb-3">
          <div className="card-body py-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div className="d-flex align-items-center gap-2">
                <Calendar size={16} className="text-muted" />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 160 }}
                  value={date}
                  max={todayValue()}
                  onChange={(e) => changeDate(e.target.value)}
                />
                {!isToday && (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => changeDate(todayValue())}>
                    Today
                  </button>
                )}
              </div>
              <ShiftChips
                value={shift}
                onChange={setShift}
                nowShift={summary?.shift}
                showNow={isToday}
              />
            </div>
            <div className="row g-2 align-items-center">
              {isManager && supervisorOptions.length > 0 && (
                <div className="col-12 col-md-4">
                  <Select
                    classNamePrefix="react-select"
                    options={supervisorOptions}
                    value={supervisorOptions.find((o) => o.value === supervisor) || null}
                    onChange={(o) => {
                      setSupervisor(o?.value || null);
                      setArea(null);
                      setCustomer(null);
                    }}
                    isClearable
                    placeholder={`All supervisors (${supervisorOptions.length - 1})`}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                </div>
              )}
              <div className="col-12 col-md-4">
                <Select
                  classNamePrefix="react-select"
                  options={areaOptions}
                  value={area ? { value: area, label: area } : null}
                  onChange={(o) => {
                    setArea(o?.value || null);
                    setCustomer(null);
                  }}
                  isClearable
                  placeholder={`All areas (${areaOptions.length})`}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-4">
                <Select
                  classNamePrefix="react-select"
                  options={customerOptions}
                  value={customerOptions.find((o) => o.value === customer) || null}
                  onChange={(o) => setCustomer(o?.value || null)}
                  isClearable
                  placeholder={`All customers (${customerOptions.length})`}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
            </div>
            {hasFilter && (
              <div className="d-flex align-items-center gap-2 mt-2 small">
                <span className="text-muted">Filtered: {filterText}</span>
                <button type="button" className="btn btn-link btn-sm p-0 ms-auto" onClick={clearFilters}>
                  <X size={13} className="me-1" />
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <h6 className="mb-0 fw-bold">{dayLabel}</h6>
              <ScopeChip isScopedToMyAreas={Boolean(summary?.isScopedToMyAreas)} />
            </div>

            {loadingSummary ? (
              <p className="text-muted small mb-0">Loading...</p>
            ) : (
              <>
                <AttendanceDonut
                  marked={figures.marked}
                  notMarked={figures.notMarked}
                  total={figures.total}
                  percent={figures.percent}
                />
                <div className="mt-3">
                  <AttendanceStatRow total={figures.total} marked={figures.marked} notMarked={figures.notMarked} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* MANAGER ONLY, matching the Flutter screen: a supervisor sees only the
            areas they are assigned to, so a per-area comparison is neither theirs
            to make nor meaningful with one area in it. The API applies the same
            restriction anyway - this is presentation on top of a real rule. */}
        {isManager && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                <BarChart2 size={18} color={MAROON} />
                <h6 className="mb-0 fw-bold">Performance</h6>
              </div>

              <div className="d-flex gap-2 mb-3">
                {GROUPINGS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`btn btn-sm rounded-pill ${grouping === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setGrouping(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {loadingPerformance && performance.length === 0 ? (
                <p className="text-muted small mb-0">Loading...</p>
              ) : performance.length === 0 ? (
                <p className="text-muted small mb-0">Nothing assigned for this shift.</p>
              ) : (
                performance.map((row) => {
                  const color = percentColor(row.percentComplete);
                  const focused = isFocused(row);
                  const isOpen = openPerfRow === row.groupName;
                  return (
                    <div
                      key={row.groupName}
                      style={{ background: focused || isOpen ? `${MAROON}0F` : "transparent" }}
                    >
                      <button
                        type="button"
                        className="btn text-start w-100 px-2 py-2"
                        title="Open this row's duty slots and narrow the page to it"
                        onClick={() => togglePerfRow(row)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="text-truncate"
                            style={{
                              fontWeight: focused ? 800 : 600,
                              fontSize: 13.5,
                              // The "(No supervisor assigned)" bucket is a real
                              // answer, not a group - areas nobody covers are the
                              // ones most worth seeing, so it is italicised rather
                              // than dropped.
                              fontStyle: row.groupKey == null ? "italic" : "normal",
                            }}
                          >
                            {row.groupName}
                          </span>
                          <span className="ms-auto text-muted flex-shrink-0" style={{ fontSize: 12.5, fontWeight: 700 }}>
                            {row.markedCount}/{row.totalAssignments}
                          </span>
                          <span className="flex-shrink-0 text-end" style={{ width: 42, fontSize: 12.5, fontWeight: 800, color }}>
                            {Math.round(row.percentComplete)}%
                          </span>
                          <span className="flex-shrink-0 text-muted">
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </span>
                        </div>
                        <div className="progress mt-2" style={{ height: 5 }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${row.totalAssignments === 0 ? 0 : (row.markedCount / row.totalAssignments) * 100}%`,
                              background: color,
                            }}
                          />
                        </div>
                      </button>
                      {isOpen && (
                        <PerformanceRowDetail
                          rows={rowsForPerfRow(row)}
                          groupName={row.groupName}
                          openGroups={openGroups}
                          onToggleGroup={toggleGroup}
                          photoUrls={photoUrls}
                          attendanceDate={summary?.attendanceDate}
                          groupByCustomer={groupByCustomer}
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <Section
          title="Marked"
          icon={CheckCircle}
          color={MARKED_GREEN}
          count={loadingAssignments && assignments.length === 0 ? null : markedRows.length}
          isOpen={openSection === "marked"}
          onToggle={() => toggleSection("marked")}
        >
          <CustomerGroups
            sectionKey="marked"
            timeline
            groups={groupByCustomer(markedRows)}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            photoUrls={photoUrls}
            attendanceDate={summary?.attendanceDate}
            emptyText="Nothing marked for this shift."
          />
        </Section>

        <Section
          title="Not Marked"
          icon={Circle}
          color={NOT_MARKED_RED}
          count={loadingAssignments && assignments.length === 0 ? null : notMarkedRows.length}
          isOpen={openSection === "notMarked"}
          onToggle={() => toggleSection("notMarked")}
        >
          <CustomerGroups
            sectionKey="notMarked"
            groups={groupByCustomer(notMarkedRows)}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            photoUrls={photoUrls}
            emptyText="Every duty slot is accounted for."
          />
        </Section>

        <Section
          title="Mismatches"
          subtitle="Guards assigned to a customer whose attendance that day doesn't match."
          icon={AlertTriangle}
          color="#E2571E"
          count={loadingMismatches && mismatches.length === 0 ? null : mismatchEmployeeCount}
          isOpen={openSection === "mismatches"}
          onToggle={() => toggleSection("mismatches")}
        >
          <MismatchGroups
            rows={filteredMismatches}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            photoUrls={photoUrls}
          />
        </Section>

        {showPrint && summary && (
          <AttendanceDayPrintModal
            show
            onClose={() => setShowPrint(false)}
            rows={filteredRows}
            attendanceDate={summary.attendanceDate}
            shift={summary.shift}
            filterText={filterText}
            branchCode={printBranchId != null ? filteredRows[0]?.branchCode : undefined}
            companyName={companyInfo?.["Company.Name"] || undefined}
          />
        )}
      </div>
    </div>
  );
};

/*
  One Performance row's own duty slots, opened inline underneath it
  (2026-09-18). Before this, tapping an area only filtered the Marked/Not
  Marked sections far below, so reading one area meant scrolling to the bottom
  of the page and back up again for the next one.

  Marked and Not Marked are each collapsible (2026-09-18, on request - they
  were fixed open at first). Not Marked comes first AND opens by default: an
  unaccounted-for guard is the reason anyone opens an area, while the marked
  list is usually the long one you want folded away.

  Both reuse `openGroups`, the same open-set the customer panels use, so the
  whole tree remembers what you had open across a refresh tick. Their keys are
  namespaced per area (`perf-<area>-...`) so two areas never share a toggle.
*/
const PerformanceRowDetail = ({
  rows, groupName, openGroups, onToggleGroup, photoUrls, attendanceDate, groupByCustomer,
}) => {
  const marked = rows.filter((r) => r.isMarked);
  const notMarked = rows.filter((r) => !r.isMarked);

  if (rows.length === 0) {
    return <p className="text-muted small px-2 pb-2 mb-0">Nothing assigned in this row for this shift.</p>;
  }

  const notMarkedKey = `perf-${groupName}-notMarked-block`;
  const markedKey = `perf-${groupName}-marked-block`;
  // Default-open for Not Marked, default-closed for Marked. `openGroups` only
  // records what has been toggled, so the Not Marked default is expressed as
  // "open unless it has been explicitly closed".
  const notMarkedOpen = !openGroups.has(notMarkedKey);
  const markedOpen = openGroups.has(markedKey);

  return (
    <div className="px-2 pb-2">
      {notMarked.length > 0 && (
        <>
          <SubBlockToggle
            label="Not Marked"
            count={notMarked.length}
            color={NOT_MARKED_RED}
            isOpen={notMarkedOpen}
            onToggle={() => onToggleGroup(notMarkedKey)}
          />
          {notMarkedOpen && (
            <CustomerGroups
              sectionKey={`perf-${groupName}-notMarked`}
              groups={groupByCustomer(notMarked)}
              openGroups={openGroups}
              onToggleGroup={onToggleGroup}
              photoUrls={photoUrls}
              emptyText="Every duty slot is accounted for."
            />
          )}
        </>
      )}
      {marked.length > 0 && (
        <>
          <SubBlockToggle
            label="Marked"
            count={marked.length}
            color={MARKED_GREEN}
            isOpen={markedOpen}
            onToggle={() => onToggleGroup(markedKey)}
          />
          {markedOpen && (
            <CustomerGroups
              sectionKey={`perf-${groupName}-marked`}
              timeline
              groups={groupByCustomer(marked)}
              openGroups={openGroups}
              onToggleGroup={onToggleGroup}
              photoUrls={photoUrls}
              attendanceDate={attendanceDate}
              emptyText="Nothing marked for this shift."
            />
          )}
        </>
      )}
    </div>
  );
};

// The Marked / Not Marked toggle inside an opened area row. Coloured pill count
// so the two read apart at a glance without needing the words.
const SubBlockToggle = ({ label, count, color, isOpen, onToggle }) => (
  <button
    type="button"
    className="btn text-start w-100 d-flex align-items-center gap-2 px-1 py-1 mt-1"
    onClick={onToggle}
  >
    <span style={{ fontSize: 12, fontWeight: 800, color, letterSpacing: 0.3 }}>
      {label.toUpperCase()}
    </span>
    <span
      className="badge rounded-pill"
      style={{ background: `${color}1F`, color, fontWeight: 800, fontSize: 11 }}
    >
      {count}
    </span>
    <span className="ms-auto" style={{ color }}>
      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
    </span>
  </button>
);

// The marked window of one customer panel as text: one mark shows a single time
// rather than "7:12 AM - 7:12 AM".
const windowLabel = (window) =>
  window.min === window.max
    ? dayjs(window.max).format("h:mm A")
    : `${dayjs(window.min).format("h:mm A")} – ${dayjs(window.max).format("h:mm A")}`;

const TABLE_HEAD_STYLE = { fontSize: 10.5, letterSpacing: 0.4, color: "#5A6472", fontWeight: 700, whiteSpace: "nowrap" };
const TIME_COL_STYLE = { width: 118, whiteSpace: "nowrap", fontSize: 12, color: "#3B4652" };
const GAP_COL_STYLE = { width: 92, whiteSpace: "nowrap" };

// Customer -> its duty slots. Two levels, because a customer with fourteen posts
// is one line to skim rather than fourteen.
//
// `timeline` (2026-09-22, "make the list a table type separating marked time and
// time taken to two different small columns"): the Marked lists render as a
// table - Customer | Marked | Time taken | Slots - so the two times line up down
// the page instead of trailing the customer name. The slot rows inside an opened
// customer use the same columns, each with its own marked time.
const CustomerGroups = ({ sectionKey, groups, openGroups, onToggleGroup, photoUrls, attendanceDate, emptyText, timeline = false }) => {
  if (groups.length === 0) return <p className="text-muted small mb-0 pt-2">{emptyText}</p>;

  if (timeline) {
    return (
      <div className="table-responsive pt-2">
        <table className="table table-sm mb-0 align-middle" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={TABLE_HEAD_STYLE}>CUSTOMER</th>
              <th style={{ ...TABLE_HEAD_STYLE, ...TIME_COL_STYLE }} title="Earliest and latest attendance marked at this customer">MARKED</th>
              <th style={{ ...TABLE_HEAD_STYLE, ...GAP_COL_STYLE }} title="Time taken to reach this customer from the previous one (this customer's first mark minus the previous customer's last mark)">TIME TAKEN</th>
              <th className="text-end" style={{ ...TABLE_HEAD_STYLE, width: 64 }}>SLOTS</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const key = `${sectionKey}-${group.key}`;
              const isOpen = openGroups.has(key);
              return (
                <React.Fragment key={key}>
                  <tr
                    role="button"
                    onClick={() => onToggleGroup(key)}
                    style={{ cursor: "pointer", background: isOpen ? "#F7F9FB" : undefined }}
                  >
                    <td style={{ minWidth: 0 }}>
                      <span className="d-inline-flex align-items-center gap-2" style={{ maxWidth: "100%" }}>
                        <Home size={14} className="text-muted flex-shrink-0" />
                        <span className="text-truncate fw-semibold" style={{ fontSize: 13.5 }}>{group.custName}</span>
                      </span>
                    </td>
                    <td style={TIME_COL_STYLE}>{group.window ? windowLabel(group.window) : <span className="text-muted">—</span>}</td>
                    <td style={GAP_COL_STYLE}>{group.gapMs != null ? <GapBadge ms={group.gapMs} /> : <span className="text-muted">—</span>}</td>
                    <td className="text-end text-nowrap">
                      <span className="text-muted fw-semibold me-1" style={{ fontSize: 12 }}>{group.rows.length}</span>
                      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </td>
                  </tr>
                  {isOpen &&
                    group.rows.map((row) => (
                      <AssignmentRow
                        key={`${row.assignmentId}-${row.employeeRecordGuid || "none"}`}
                        row={row}
                        photoUrls={photoUrls}
                        attendanceDate={attendanceDate}
                        asTableRow
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="pt-2">
      {groups.map((group) => {
        const key = `${sectionKey}-${group.key}`;
        const isOpen = openGroups.has(key);
        return (
          <div key={key} className="border-bottom">
            <button
              type="button"
              className="btn text-start w-100 d-flex align-items-center gap-2 px-1 py-2"
              onClick={() => onToggleGroup(key)}
            >
              <Home size={14} className="text-muted flex-shrink-0" />
              <span className="text-truncate fw-semibold" style={{ fontSize: 13.5 }}>{group.custName}</span>
              {/* When this site was worked, without opening it. One mark shows a
                  single time rather than "7:12 AM - 7:12 AM". */}
              {group.window && (
                <span
                  className="text-nowrap flex-shrink-0 d-none d-sm-inline"
                  style={{ fontSize: 11.5, color: "#5A6472" }}
                  title="Earliest and latest attendance marked at this customer"
                >
                  <Clock size={11} className="me-1" style={{ verticalAlign: -1 }} />
                  {windowLabel(group.window)}
                </span>
              )}
              {/* How long the visitor took to get HERE from the previous site -
                  the thing a supervisor is actually looking for when scanning
                  the list. */}
              {group.gapMs != null && <GapBadge ms={group.gapMs} />}
              <span className="ms-auto text-muted fw-semibold" style={{ fontSize: 12 }}>{group.rows.length}</span>
              {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isOpen && (
              <div className="pb-2">
                {group.rows.map((row) => (
                  <AssignmentRow
                    key={`${row.assignmentId}-${row.employeeRecordGuid || "none"}`}
                    row={row}
                    photoUrls={photoUrls}
                    attendanceDate={attendanceDate}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AssignmentRow = ({ row, photoUrls, attendanceDate, asTableRow = false }) => {
  const markedAt = row.isMarked ? formatMarkedAt(row.capturedDate, attendanceDate) : null;
  const hasScanLocation = row.isMarked && row.scanLatitude != null && row.scanLongitude != null;

  const avatar =
    row.isMarked && row.employeeRecordGuid ? (
      <Avatar photoUrl={photoUrls[row.employeeRecordGuid]} first={row.empName} last={row.empLastName} />
    ) : (
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 30, height: 30, borderRadius: "50%", background: "#F1F3F5" }}
      >
        <X size={14} color="#9AA3AD" />
      </div>
    );

  // How the punch was matched, the QR-scan location, temp/permanent - the same
  // trailing controls in both layouts.
  const trailing = (
    <>
      {row.isMarked && row.matchMethod && (
        <span className="text-muted flex-shrink-0" style={{ fontSize: 10.5, letterSpacing: 0.3 }}>
          {row.matchMethod.toUpperCase()}
        </span>
      )}
      {hasScanLocation ? (
        <a
          href={directionsUrl(row.scanLatitude, row.scanLongitude)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 py-0 px-2 flex-shrink-0"
          style={{ fontSize: 11 }}
          title={
            `Marked from the QR scan at ${dayjs(row.scannedAt).format("h:mm A")}` +
            (row.scanAccuracyMeters != null ? ` (±${Math.round(row.scanAccuracyMeters)} m)` : "")
          }
        >
          <Navigation size={11} />
          Navigate
        </a>
      ) : (
        row.isMarked && (
          <span
            className="text-muted flex-shrink-0"
            style={{ fontSize: 10.5 }}
            title="Marked without scanning the customer's QR code, so no location was recorded"
          >
            No location
          </span>
        )
      )}
      <TempBadge isTemp={row.isTemp} />
    </>
  );

  if (asTableRow) {
    return (
      <tr style={{ background: "#FBFCFD" }}>
        <td style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2 ps-4">
            {avatar}
            <div style={{ minWidth: 0 }}>
              <div className="text-truncate" style={{ fontSize: 13 }}>{row.assignmentName || "Duty Slot"}</div>
              {row.isMarked ? (
                <div className="text-muted text-truncate" style={{ fontSize: 11.5 }}>
                  {fullName(row.empName, row.empLastName) || "—"} ({row.empCode})
                </div>
              ) : (
                <div style={{ fontSize: 11.5, color: NOT_MARKED_RED }}>Not marked</div>
              )}
            </div>
          </div>
        </td>
        <td style={TIME_COL_STYLE} title="When the attendance was marked">
          {markedAt || <span className="text-muted">—</span>}
        </td>
        <td style={GAP_COL_STYLE} />
        <td className="text-end">
          <div className="d-inline-flex align-items-center gap-2 justify-content-end">{trailing}</div>
        </td>
      </tr>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 ps-4 pe-1 py-1">
      {row.isMarked && row.employeeRecordGuid ? (
        <Avatar photoUrl={photoUrls[row.employeeRecordGuid]} first={row.empName} last={row.empLastName} />
      ) : (
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 30, height: 30, borderRadius: "50%", background: "#F1F3F5" }}
        >
          <X size={14} color="#9AA3AD" />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="text-truncate" style={{ fontSize: 13 }}>{row.assignmentName || "Duty Slot"}</div>
        {row.isMarked ? (
          <div className="text-muted text-truncate" style={{ fontSize: 11.5 }}>
            {fullName(row.empName, row.empLastName) || "—"} ({row.empCode})
            {markedAt && (
              <span className="ms-2 text-nowrap" title="When the attendance was marked">
                <Clock size={11} className="me-1" style={{ verticalAlign: -1 }} />
                {markedAt}
              </span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: NOT_MARKED_RED }}>Not marked</div>
        )}
      </div>
      {/* How the punch was matched - a fingerprint match and a manual mark are not
          the same evidence that a guard was there, so it is named rather than
          iconified. The location is the supervisor's QR scan at this customer just
          before marking; a slot marked from the name search has no scan, so it
          says so rather than offering directions to nowhere. */}
      <div className="ms-auto d-flex align-items-center gap-2">{trailing}</div>
    </div>
  );
};

// Employee -> the assignments they are posted to but did not mark at. Grouped by
// employee because the question is "where is this guard", not "what happened at
// this post".
const MismatchGroups = ({ rows, openGroups, onToggleGroup, photoUrls }) => {
  if (rows.length === 0) return <p className="text-muted small mb-0 pt-2">No mismatches right now.</p>;

  const groups = new Map();
  rows.forEach((row) => {
    if (!groups.has(row.employeeRecordGuid)) groups.set(row.employeeRecordGuid, []);
    groups.get(row.employeeRecordGuid).push(row);
  });

  return (
    <div className="pt-2">
      {[...groups.entries()].map(([guid, employeeRows]) => {
        const key = `mismatch-${guid}`;
        const isOpen = openGroups.has(key);
        const first = employeeRows[0];
        const name = fullName(first.empName, first.empLastName);
        return (
          <div key={key} className="border-bottom">
            <button
              type="button"
              className="btn text-start w-100 d-flex align-items-center gap-2 px-1 py-2"
              onClick={() => onToggleGroup(key)}
            >
              <Avatar photoUrl={photoUrls[guid]} first={first.empName} last={first.empLastName} />
              <span className="text-truncate fw-semibold" style={{ fontSize: 13.5 }}>
                {name || `Employee ${first.empCode}`} ({first.empCode})
              </span>
              <span className="ms-auto text-muted fw-semibold" style={{ fontSize: 12 }}>{employeeRows.length}</span>
              {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isOpen && (
              <div className="pb-2 ps-4 pe-1">
                {employeeRows.map((row, idx) => (
                  <div key={`${row.assignedCustId}-${idx}`} className="rounded p-2 mb-2" style={{ background: "#F6F7F9" }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>
                        Assigned: {row.assignedCustName || "Unknown"} ({row.assignedArea || "—"})
                      </span>
                      <span className="ms-auto"><TempBadge isTemp={row.assignedIsTemp} /></span>
                    </div>
                    <div className="d-flex align-items-center gap-1 mt-1" style={{ color: "#E2571E" }}>
                      {row.markedCustId == null ? <X size={13} /> : <AlertTriangle size={13} />}
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {row.markedCustId == null
                          ? "Not marked anywhere today"
                          : `Marked: ${row.markedCustName || "Unknown"} (${row.markedArea || "—"})`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AttendanceDashboardPage;
